import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";

/** One write of a settings row: its value and lock, or back to the inherited value. */
export type SettingAction = { type: "put"; value: unknown; locked: boolean } | { type: "delete" };

/** How long "Saved" shows after a write (D2). */
export const SAVED_MS = 2000;

/**
 * The save of one settings row (openspec change settings-typed-controls, D2): one write at a
 * time; while it runs (and the refetch after it, so the row never flashes the old value) the
 * row shows the pending value and "Saving…"; then "Saved" for a moment. A refused write drops
 * the pending value, so the control shows the server's, and keeps the refusal for the row.
 */
export function useSettingSave(opts: {
  run: (action: SettingAction) => Promise<unknown>;
  /** Refetches what the write changed; awaited before the row leaves "Saving…". */
  settle: () => Promise<unknown>;
}) {
  const mutation = useMutation({
    mutationFn: opts.run,
    onSettled: () => opts.settle(),
  });
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!saved) {
      return;
    }
    const timer = window.setTimeout(() => setSaved(false), SAVED_MS);
    return () => window.clearTimeout(timer);
  }, [saved]);
  const pending = mutation.isPending ? mutation.variables : undefined;
  return {
    /** The write running, if any. */
    pending,
    busy: mutation.isPending,
    state: mutation.isPending
      ? ("saving" as const)
      : saved
        ? ("saved" as const)
        : ("idle" as const),
    error: mutation.error,
    /** Runs `action` unless a write is running; `onSaved` once it succeeded and refetched. */
    save(action: SettingAction, onSaved?: () => void) {
      if (mutation.isPending) {
        return;
      }
      setSaved(false);
      mutation.mutate(action, {
        onSuccess: () => {
          setSaved(true);
          onSaved?.();
        },
      });
    },
    /**
     * Forgets a refusal (an editor opened again starts clean). Never while a write runs:
     * resetting would detach it, and the next save could overwrite it.
     */
    clear() {
      if (!mutation.isPending) {
        mutation.reset();
      }
    },
  };
}
